import type { CSSProperties } from 'react';
import { Link } from 'react-router-dom';
import { Seo } from '../components/Seo';
import { Icon } from '../components/Icon';
import { Reveal } from '../components/Reveal';
import { CoinAvatar } from '../components/CoinAvatar';
import { ExchangeForm } from '../components/Exchange/ExchangeForm';
import {
  TrustBar, StatsBand, PopularPairs, HowItWorksSteps, FaqList, CtaSection,
} from '../components/Sections';
import { useT } from '../i18n';
import { FEATURED_COINS } from '../lib/content';
import { buildTitle } from '../lib/site';

const ORBIT = [
  { ticker: 'btc', ox: '46%', oy: '-7%' },
  { ticker: 'eth', ox: '-9%', oy: '8%' },
  { ticker: 'sol', ox: '102%', oy: '38%' },
  { ticker: 'xmr', ox: '-14%', oy: '74%' },
  { ticker: 'usdt_trx', ox: '108%', oy: '86%' },
];

export function HomePage() {
  const t = useT();
  return (
    <>
      <Seo
        title={buildTitle('')}
        description={t.home.heroLead}
        path="/"
      />

      {/* HERO */}
      <section className="hero">
        <div className="container">
          <div className="hero-grid">
            <div className="hero-copy">
              <img className="hero-logo fade-up" src="/logo-mark.png" alt="ShieldMyAss" width={72} height={72} />
              <span className="eyebrow fade-up">{t.home.heroEyebrow}</span>
              <h1 className="fade-up d1">
                {t.home.heroTitlePre}<br />
                {t.home.heroTitleMid}&nbsp;
                <span className="accent">{t.home.heroTitleAccent}</span><br />
                {t.home.heroTitlePost}
              </h1>
              <p className="lead fade-up d2">{t.home.heroLead}</p>
              <div className="hero-cta fade-up d3">
                <a href="#exchange" className="btn btn-primary btn-large">
                  {t.home.startSwapping}
                  <Icon name="arrow-right" width={18} height={18} />
                </a>
                <Link to="/how-it-works" className="btn btn-outline btn-large">
                  {t.home.howItWorks}
                </Link>
              </div>
              <div className="hero-stats fade-up d4">
                <div className="stat">
                  <div className="num">900<span className="unit">+</span></div>
                  <div className="label">{t.home.heroStats.coins}</div>
                </div>
                <div className="stat">
                  <div className="num">5<span className="unit">min</span></div>
                  <div className="label">{t.home.heroStats.avgSettle}</div>
                </div>
                <div className="stat">
                  <div className="num">0<span className="unit">%</span></div>
                  <div className="label">{t.home.heroStats.kyc}</div>
                </div>
                <div className="stat">
                  <div className="num">24<span className="unit">/7</span></div>
                  <div className="label">{t.home.heroStats.liveRates}</div>
                </div>
              </div>
            </div>

            <div className="hero-widget">
              <div className="widget-glow" />
              <div className="hero-orbit" aria-hidden="true">
                {ORBIT.map((c) => (
                  <span
                    key={c.ticker}
                    className="orbit-coin"
                    style={{ '--ox': c.ox, '--oy': c.oy } as CSSProperties}
                  >
                    <CoinAvatar ticker={c.ticker} size={46} />
                  </span>
                ))}
              </div>
              <div id="exchange" className="exchange-widget-card">
                <ExchangeForm />
              </div>
              <aside className="home-operator-note" aria-label={t.home.exchangeOperatorHeading}>
                <img src="/changenow-logo.png" alt="ChangeNOW logo" width={42} height={42} />
                <div>
                  <p>{t.home.exchangeOperatorNote}</p>
                  <a href="https://changenow.com/contact" target="_blank" rel="noopener noreferrer">
                    {t.home.transactionSupportCta}
                    <Icon name="arrow-right" width={14} height={14} />
                  </a>
                </div>
              </aside>
            </div>
          </div>
        </div>
      </section>

      {/* TRUST BAR */}
      <section className="section-tight">
        <div className="container">
          <TrustBar />
        </div>
      </section>

      <div className="divider-line" />

      {/* STATS BAND */}
      <section className="section-tight">
        <div className="container">
          <StatsBand />
        </div>
      </section>

      {/* POPULAR PAIRS */}
      <section className="section">
        <div className="container">
          <Reveal>
            <div className="section-head">
              <span className="eyebrow">{t.home.marketsEyebrow}</span>
              <h2>{t.home.marketsTitle}</h2>
              <p>{t.home.marketsDesc}</p>
            </div>
          </Reveal>
          <Reveal delay={80}>
            <PopularPairs />
          </Reveal>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="section" style={{ background: 'linear-gradient(180deg, transparent, rgba(245,181,68,0.02), transparent)' }}>
        <div className="container">
          <div className="section-head center">
            <span className="eyebrow">{t.home.processEyebrow}</span>
            <h2>{t.home.processTitle}</h2>
            <p>{t.home.processDesc}</p>
          </div>
          <HowItWorksSteps />
        </div>
      </section>

      {/* FEATURED COINS */}
      <section className="section">
        <div className="container">
          <div className="section-head">
            <span className="eyebrow">{t.home.assetsEyebrow}</span>
            <h2>{t.home.assetsTitle}</h2>
            <p>{t.home.assetsDesc}</p>
          </div>
          <div className="grid grid-3">
            {FEATURED_COINS.slice(0, 6).map((c, i) => (
              <Reveal key={c.ticker} delay={i * 70}>
                <div className="card card-feature">
                  <div className="card-icon">
                    <CoinAvatar ticker={c.ticker} size={26} ring />
                  </div>
                  <h3>{c.name} <span className="mono" style={{ color: 'var(--gold)', fontSize: 14 }}>{c.symbol}</span></h3>
                  <p>{t.featuredBlurbs[c.ticker as keyof typeof t.featuredBlurbs] ?? c.blurb}</p>
                  <Link
                    to={`/exchange/${c.ticker}-to-btc`}
                    style={{ marginTop: 'auto', fontSize: 13, color: 'var(--gold)', display: 'inline-flex', gap: 6, alignItems: 'center' }}
                  >
                    {c.symbol} <Icon name="arrow-right" width={14} height={14} />
                  </Link>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="section" style={{ background: 'linear-gradient(180deg, transparent, rgba(245,181,68,0.02), transparent)' }}>
        <div className="container">
          <div className="section-head">
            <span className="eyebrow">{t.home.whyEyebrow}</span>
            <h2>{t.home.whyTitle}</h2>
            <p>{t.home.whyDesc}</p>
          </div>
          <div className="grid grid-4">
            {t.trustPoints.map((item, i) => (
              <Reveal key={item.title} delay={i * 70}>
                <div className="card card-feature">
                  <div className="card-icon"><Icon name={item.icon as never} /></div>
                  <h3>{item.title}</h3>
                  <p>{item.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ PREVIEW */}
      <section className="section">
        <div className="container container-narrow">
          <div className="section-head center">
            <span className="eyebrow">{t.home.questionsEyebrow}</span>
            <h2>{t.home.questionsTitle}</h2>
          </div>
          <FaqList limit={5} />
          <div style={{ textAlign: 'center', marginTop: 32 }}>
            <Link to="/faq" className="btn btn-ghost btn-large">
              {t.home.seeAllFaqs}
              <Icon name="arrow-right" width={16} height={16} />
            </Link>
          </div>
        </div>
      </section>

      <CtaSection />
    </>
  );
}
